<script setup lang="ts">
/**
 * La couche où volent les pièces (cf. lib/coinFlight).
 *
 * Montée une fois au-dessus de toute l'app : le point de départ (une case dans
 * une liste qui défile) et l'arrivée (la pastille de la jauge) ne partagent
 * aucun conteneur. La pièce est LA pièce de l'app (SpinningCoin), pas un rond
 * jaune de plus.
 *
 * Elle vole en arc, à la manière d'un objet lancé : elle monte d'abord, puis
 * tombe dans le compteur, qui rebondit et sonne. Moins d'une seconde, jamais
 * un clic bloqué (`pointer-events: none`). Si l'appareil demande moins
 * d'animations, la pièce ne vole pas : le compteur rebondit seul.
 */
import { nextTick, onMounted, onUnmounted, ref } from 'vue'
import SpinningCoin from '@/components/coins/SpinningCoin.vue'
import { coinTarget } from '@/lib/coinFlight'
import { playSound } from '@/lib/sound'
import { eventBus } from '@/utils/eventBus'

interface Flight { id: number; x: number; y: number }

const COIN = 26
const FLIGHT_MS = 640

const flights = ref<Flight[]>([])
const nodes = new Map<number, HTMLElement>()
let seq = 0

const reducedMotion = () =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

function bump(target: HTMLElement) {
  target.animate(
    [{ transform: 'scale(1)' }, { transform: 'scale(1.18)' }, { transform: 'scale(0.96)' }, { transform: 'scale(1)' }],
    { duration: 360, easing: 'cubic-bezier(0.2, 0.9, 0.25, 1.3)' },
  )
  void playSound('coin')
}

async function launch(detail: { x: number; y: number }) {
  const target = coinTarget()
  if (!target) return
  if (reducedMotion()) {
    bump(target)
    return
  }

  const id = ++seq
  flights.value = [...flights.value, { id, x: detail.x, y: detail.y }]
  await nextTick()
  const el = nodes.get(id)
  if (!el) return

  const to = target.getBoundingClientRect()
  const dx = to.left + Math.min(to.width / 2, 22) - detail.x
  const dy = to.top + to.height / 2 - detail.y
  // L'arc : le sommet est au-dessus du plus haut des deux points.
  const lift = Math.min(-60, dy / 2 - 70)

  const anim = el.animate(
    [
      { transform: 'translate(0, 0) scale(0.6)', opacity: 0 },
      { transform: `translate(${dx * 0.15}px, -24px) scale(1.15)`, opacity: 1, offset: 0.18 },
      { transform: `translate(${dx * 0.55}px, ${lift}px) scale(1)`, offset: 0.55 },
      { transform: `translate(${dx}px, ${dy}px) scale(0.7)`, opacity: 1 },
    ],
    { duration: FLIGHT_MS, easing: 'cubic-bezier(0.45, 0, 0.55, 1)', fill: 'forwards' },
  )
  anim.onfinish = () => {
    flights.value = flights.value.filter(f => f.id !== id)
    nodes.delete(id)
    const live = coinTarget()
    if (live) bump(live)
  }
}

function bind(id: number, el: unknown) {
  if (el instanceof HTMLElement) nodes.set(id, el)
}

let off: (() => void) | null = null
onMounted(() => { off = eventBus.on('app:coin-flight', (d) => { void launch(d) }) })
onUnmounted(() => off?.())
</script>

<template>
  <div class="pointer-events-none fixed inset-0 z-[19980]" aria-hidden="true">
    <div v-for="f in flights" :key="f.id" :ref="(el) => bind(f.id, el)" class="absolute"
      :style="{ left: `${f.x - COIN / 2}px`, top: `${f.y - COIN / 2}px`, width: `${COIN}px`, height: `${COIN}px` }">
      <SpinningCoin :size="COIN" :duration="0.5" />
    </div>
  </div>
</template>
